import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  Radar, Anchor, MapPin, Plus, X, Calendar, Camera, Video,
  Trash2, Edit3, ChevronDown, ChevronUp, AlertTriangle, Waves,
  Thermometer, Eye, Ship, FileText, Upload, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipi di sito
const SITE_TYPES = [
  { value: 'relitto', label: 'Relitto', icon: Anchor },
  { value: 'tegnua', label: 'Tegnùa', icon: Waves },
  { value: 'fondale_roccioso', label: 'Fondale Roccioso', icon: MapPin },
  { value: 'altro', label: 'Altro', icon: Radar }
];

const ROVSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState({ total_missions: 0, missions_with_nets: 0, total_nets_kg: 0, unique_sites: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [expandedMissions, setExpandedMissions] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    mission_date: new Date().toISOString().split('T')[0],
    site_name: '',
    site_type: 'relitto',
    location: { lat: '', lng: '' },
    depth_meters: '',
    ghost_nets_found: false,
    nets_description: '',
    nets_estimated_kg: '',
    other_debris: '',
    media_urls: [],
    notes: '',
    organization: '',
    operator_name: '',
    rov_model: '',
    visibility_meters: '',
    water_temp_celsius: ''
  });
  
  const [newMediaUrl, setNewMediaUrl] = useState('');

  const fetchMissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rov-missions`);
      setMissions(response.data.missions || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching missions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const resetForm = () => {
    setFormData({
      mission_date: new Date().toISOString().split('T')[0],
      site_name: '',
      site_type: 'relitto',
      location: { lat: '', lng: '' },
      depth_meters: '',
      ghost_nets_found: false,
      nets_description: '',
      nets_estimated_kg: '',
      other_debris: '',
      media_urls: [],
      notes: '',
      organization: '',
      operator_name: '',
      rov_model: '',
      visibility_meters: '',
      water_temp_celsius: ''
    });
    setNewMediaUrl('');
    setEditingMission(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.site_name || !formData.location.lat || !formData.location.lng) {
      toast.error('Compila nome sito e coordinate');
      return;
    }

    try {
      const payload = {
        ...formData,
        location: {
          lat: parseFloat(formData.location.lat),
          lng: parseFloat(formData.location.lng)
        },
        depth_meters: formData.depth_meters ? parseFloat(formData.depth_meters) : null,
        nets_estimated_kg: formData.nets_estimated_kg ? parseFloat(formData.nets_estimated_kg) : null,
        visibility_meters: formData.visibility_meters ? parseFloat(formData.visibility_meters) : null,
        water_temp_celsius: formData.water_temp_celsius ? parseFloat(formData.water_temp_celsius) : null
      };

      if (editingMission) {
        await axios.put(`${API_URL}/api/rov-missions/${editingMission}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Missione aggiornata!');
      } else {
        await axios.post(`${API_URL}/api/rov-missions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Missione ROV registrata!');
      }
      
      setShowForm(false);
      resetForm();
      fetchMissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleEdit = (mission) => {
    setFormData({
      mission_date: mission.mission_date || new Date().toISOString().split('T')[0],
      site_name: mission.site_name || '',
      site_type: mission.site_type || 'relitto',
      location: mission.location || { lat: '', lng: '' },
      depth_meters: mission.depth_meters?.toString() || '',
      ghost_nets_found: mission.ghost_nets_found || false,
      nets_description: mission.nets_description || '',
      nets_estimated_kg: mission.nets_estimated_kg?.toString() || '',
      other_debris: mission.other_debris || '',
      media_urls: mission.media_urls || [],
      notes: mission.notes || '',
      organization: mission.organization || '',
      operator_name: mission.operator_name || '',
      rov_model: mission.rov_model || '',
      visibility_meters: mission.visibility_meters?.toString() || '',
      water_temp_celsius: mission.water_temp_celsius?.toString() || ''
    });
    setEditingMission(mission.id);
    setShowForm(true);
  };

  const handleDelete = async (missionId) => {
    if (!window.confirm('Eliminare questa missione?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/rov-missions/${missionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Missione eliminata');
      fetchMissions();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const addMediaUrl = () => {
    if (newMediaUrl && !formData.media_urls.includes(newMediaUrl)) {
      setFormData({
        ...formData,
        media_urls: [...formData.media_urls, newMediaUrl]
      });
      setNewMediaUrl('');
    }
  };

  const removeMediaUrl = (url) => {
    setFormData({
      ...formData,
      media_urls: formData.media_urls.filter(u => u !== url)
    });
  };

  const toggleExpand = (missionId) => {
    setExpandedMissions(prev => ({
      ...prev,
      [missionId]: !prev[missionId]
    }));
  };

  const getSiteIcon = (siteType) => {
    const type = SITE_TYPES.find(t => t.value === siteType);
    return type ? type.icon : Radar;
  };

  const getSiteLabel = (siteType) => {
    const type = SITE_TYPES.find(t => t.value === siteType);
    return type ? type.label : siteType;
  };

  // Ottieni posizione GPS corrente
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            location: {
              lat: position.coords.latitude.toFixed(6),
              lng: position.coords.longitude.toFixed(6)
            }
          });
          toast.success('Posizione rilevata!');
        },
        (error) => {
          toast.error('Impossibile rilevare la posizione');
        }
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30">
          <Radar className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="MISSIONI ROV" />
        </h2>
        <p className="text-cyan-400 mt-1">
          <TranslatedText text="Monitoraggio Reti Fantasma" />
        </p>
        
        {/* Badge */}
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-500/40 rounded-full">
          <Anchor className="w-4 h-4 text-blue-400" />
          <span className="text-blue-300 text-xs font-medium">
            <TranslatedText text="Relitti • Tegnùe • Fondali" />
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-slate-900/60 border border-blue-500/30 rounded-xl p-4 text-center">
          <Radar className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_missions}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Missioni" /></p>
        </div>
        
        <div className="bg-slate-900/60 border border-red-500/30 rounded-xl p-4 text-center">
          <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.missions_with_nets}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Con Reti" /></p>
        </div>
        
        <div className="bg-slate-900/60 border border-amber-500/30 rounded-xl p-4 text-center">
          <Waves className="w-6 h-6 text-amber-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.total_nets_kg} <span className="text-sm font-normal">kg</span></p>
          <p className="text-xs text-slate-400"><TranslatedText text="Reti Stimate" /></p>
        </div>
        
        <div className="bg-slate-900/60 border border-emerald-500/30 rounded-xl p-4 text-center">
          <MapPin className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{stats.unique_sites}</p>
          <p className="text-xs text-slate-400"><TranslatedText text="Siti Monitorati" /></p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
        <p className="text-blue-300 text-sm">
          <TranslatedText text="Questa sezione è dedicata ai ricercatori subacquei certificati. Carica i dati delle tue ricognizioni ROV per mappare la presenza di reti fantasma su relitti e tegnùe del Mediterraneo." />
        </p>
      </div>

      {/* Pulsante Aggiungi */}
      <button
        onClick={() => { resetForm(); setShowForm(true); }}
        className="w-full mb-6 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg"
      >
        <Plus className="w-5 h-5" />
        <TranslatedText text="Registra Missione ROV" />
      </button>

      {/* Lista Missioni */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-400" />
          <TranslatedText text="Ricognizioni Registrate" />
        </h3>
        
        {missions.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Radar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p><TranslatedText text="Nessuna missione ROV registrata" /></p>
          </div>
        ) : (
          missions.map((mission) => {
            const SiteIcon = getSiteIcon(mission.site_type);
            const isExpanded = expandedMissions[mission.id];
            
            return (
              <div key={mission.id} className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-all"
                  onClick={() => toggleExpand(mission.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      mission.ghost_nets_found ? 'bg-red-500/20' : 'bg-blue-500/20'
                    }`}>
                      <SiteIcon className={`w-5 h-5 ${mission.ghost_nets_found ? 'text-red-400' : 'text-blue-400'}`} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{mission.site_name}</p>
                      <p className="text-sm text-slate-400 flex items-center gap-2">
                        <span>{getSiteLabel(mission.site_type)}</span>
                        {mission.depth_meters && (
                          <>
                            <span className="text-slate-600">•</span>
                            <span>{mission.depth_meters}m</span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {mission.ghost_nets_found && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Reti trovate
                      </span>
                    )}
                    <span className="text-sm text-slate-500">
                      {new Date(mission.mission_date).toLocaleDateString('it-IT')}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-4 bg-slate-800/30 space-y-4">
                    {/* Dettagli missione */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {mission.depth_meters && (
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Profondità</p>
                          <p className="text-blue-400 font-bold">{mission.depth_meters}m</p>
                        </div>
                      )}
                      {mission.visibility_meters && (
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Visibilità</p>
                          <p className="text-cyan-400 font-bold">{mission.visibility_meters}m</p>
                        </div>
                      )}
                      {mission.water_temp_celsius && (
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Temperatura</p>
                          <p className="text-amber-400 font-bold">{mission.water_temp_celsius}°C</p>
                        </div>
                      )}
                      {mission.nets_estimated_kg && (
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Reti Stimate</p>
                          <p className="text-red-400 font-bold">{mission.nets_estimated_kg} kg</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Descrizione reti */}
                    {mission.ghost_nets_found && mission.nets_description && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <p className="text-xs text-red-400 uppercase mb-1">Reti Fantasma Rilevate</p>
                        <p className="text-slate-300 text-sm">{mission.nets_description}</p>
                      </div>
                    )}
                    
                    {/* Altri rifiuti */}
                    {mission.other_debris && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                        <p className="text-xs text-amber-400 uppercase mb-1">Altri Rifiuti</p>
                        <p className="text-slate-300 text-sm">{mission.other_debris}</p>
                      </div>
                    )}
                    
                    {/* Note */}
                    {mission.notes && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase mb-1">Note</p>
                        <p className="text-slate-300 text-sm">{mission.notes}</p>
                      </div>
                    )}
                    
                    {/* Media */}
                    {mission.media_urls && mission.media_urls.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase mb-2">Media ({mission.media_urls.length})</p>
                        <div className="flex flex-wrap gap-2">
                          {mission.media_urls.map((url, idx) => (
                            <a 
                              key={idx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-cyan-400 text-xs flex items-center gap-1"
                            >
                              {url.includes('video') || url.includes('.mp4') || url.includes('.mov') ? (
                                <Video className="w-3 h-3" />
                              ) : (
                                <ImageIcon className="w-3 h-3" />
                              )}
                              Media {idx + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Info operatore */}
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-2 border-t border-slate-700/50">
                      {mission.operator_name && (
                        <span>Operatore: {mission.operator_name}</span>
                      )}
                      {mission.organization && (
                        <span>Ente: {mission.organization}</span>
                      )}
                      {mission.rov_model && (
                        <span>ROV: {mission.rov_model}</span>
                      )}
                    </div>
                    
                    {/* Azioni */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(mission); }}
                        className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg text-sm flex items-center gap-1"
                      >
                        <Edit3 className="w-4 h-4" />
                        Modifica
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(mission.id); }}
                        className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Elimina
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-blue-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-blue-400">
                {editingMission ? 'Modifica Missione' : 'Nuova Missione ROV'}
              </h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Data e Sito */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase">Data Missione *</label>
                  <input
                    type="date"
                    value={formData.mission_date}
                    onChange={(e) => setFormData({...formData, mission_date: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">Tipo Sito</label>
                  <select
                    value={formData.site_type}
                    onChange={(e) => setFormData({...formData, site_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  >
                    {SITE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Nome Sito *</label>
                <input
                  type="text"
                  value={formData.site_name}
                  onChange={(e) => setFormData({...formData, site_name: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  placeholder="Es: Relitto Paguro, Tegnùa di Chioggia"
                />
              </div>
              
              {/* Coordinate */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400 uppercase">Coordinate *</label>
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                  >
                    <MapPin className="w-3 h-3" />
                    Rileva GPS
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={formData.location.lat}
                    onChange={(e) => setFormData({...formData, location: {...formData.location, lat: e.target.value}})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Latitudine"
                  />
                  <input
                    type="text"
                    value={formData.location.lng}
                    onChange={(e) => setFormData({...formData, location: {...formData.location, lng: e.target.value}})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Longitudine"
                  />
                </div>
              </div>
              
              {/* Parametri immersione */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase">Profondità (m)</label>
                  <input
                    type="number"
                    value={formData.depth_meters}
                    onChange={(e) => setFormData({...formData, depth_meters: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="25"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">Visibilità (m)</label>
                  <input
                    type="number"
                    value={formData.visibility_meters}
                    onChange={(e) => setFormData({...formData, visibility_meters: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase">Temp. (°C)</label>
                  <input
                    type="number"
                    value={formData.water_temp_celsius}
                    onChange={(e) => setFormData({...formData, water_temp_celsius: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="18"
                  />
                </div>
              </div>
              
              {/* Reti Fantasma */}
              <div className="border border-red-500/30 rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ghost_nets_found}
                    onChange={(e) => setFormData({...formData, ghost_nets_found: e.target.checked})}
                    className="w-5 h-5 rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500"
                  />
                  <span className="text-red-400 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Reti Fantasma Rilevate
                  </span>
                </label>
                
                {formData.ghost_nets_found && (
                  <>
                    <div>
                      <label className="text-xs text-slate-400 uppercase">Descrizione Reti</label>
                      <textarea
                        value={formData.nets_description}
                        onChange={(e) => setFormData({...formData, nets_description: e.target.value})}
                        className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                        rows={2}
                        placeholder="Tipo, estensione, stato..."
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 uppercase">Peso Stimato (kg)</label>
                      <input
                        type="number"
                        value={formData.nets_estimated_kg}
                        onChange={(e) => setFormData({...formData, nets_estimated_kg: e.target.value})}
                        className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                        placeholder="50"
                      />
                    </div>
                  </>
                )}
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Altri Rifiuti Osservati</label>
                <textarea
                  value={formData.other_debris}
                  onChange={(e) => setFormData({...formData, other_debris: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  rows={2}
                  placeholder="Plastica, pneumatici, ancore abbandonate..."
                />
              </div>
              
              {/* Media URLs */}
              <div>
                <label className="text-xs text-slate-400 uppercase mb-2 block">Foto/Video (URL)</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="url"
                    value={newMediaUrl}
                    onChange={(e) => setNewMediaUrl(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={addMediaUrl}
                    className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                {formData.media_urls.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.media_urls.map((url, idx) => (
                      <span key={idx} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300 flex items-center gap-1">
                        Media {idx + 1}
                        <button type="button" onClick={() => removeMediaUrl(url)} className="text-red-400 hover:text-red-300">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Info Operatore */}
              <div className="border border-dashed border-slate-600 rounded-lg p-4 space-y-3">
                <p className="text-xs text-slate-400 uppercase flex items-center gap-2">
                  <Ship className="w-4 h-4 text-cyan-400" />
                  Info Operatore (opzionale)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Nome Operatore</label>
                    <input
                      type="text"
                      value={formData.operator_name}
                      onChange={(e) => setFormData({...formData, operator_name: e.target.value})}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Modello ROV</label>
                    <input
                      type="text"
                      value={formData.rov_model}
                      onChange={(e) => setFormData({...formData, rov_model: e.target.value})}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                      placeholder="BlueROV2"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Ente/Organizzazione</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({...formData, organization: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                    placeholder="Es: ARPAV, CNR, Università..."
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-slate-400 uppercase">Note</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  rows={2}
                  placeholder="Osservazioni aggiuntive..."
                />
              </div>
              
              <button
                type="submit"
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg"
              >
                {editingMission ? 'Aggiorna Missione' : 'Salva Missione'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROVSection;
